import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EneaflixWordmark from '../components/EneaflixWordmark';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Cache key & TTL — listing is served as lightweight thumbs, ~30KB total,
// safe to keep in sessionStorage between navigations on the same tab.
const CACHE_KEY = 'eneaflix:list:v2';
const CACHE_TTL_MS = 5 * 60 * 1000;

const PAGE_SIZE = 12; // initial cards rendered before "Carregar mais"

const ENNEAGRAM_TYPES = [
  'Tipo 1', 'Tipo 2', 'Tipo 3', 'Tipo 4', 'Tipo 5',
  'Tipo 6', 'Tipo 7', 'Tipo 8', 'Tipo 9',
];

/**
 * Single poster card — keeps a local "loaded" state so the dark
 * placeholder (icon + caption) stays visible until the thumbnail
 * finishes decoding, then fades in over 600ms.
 */
const PosterCard = ({ item }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <Link
      to={`/eneaflix/${item.eneaflix_id}`}
      data-testid={`eneaflix-card-${item.eneaflix_id}`}
      className="group relative block rounded-xl overflow-hidden bg-[#1A1A1A] border border-transparent hover:border-accent-orange transition-colors duration-300"
    >
      <div className="relative aspect-[2/3] bg-[#0A0A0A] overflow-hidden">
        {/* Persistent placeholder — visible until <img> loads */}
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center p-3 text-center bg-gradient-to-br from-[#1F1F1F] to-[#000000] transition-opacity duration-500 ${
            item.image && loaded ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <span className="text-[9px] font-mono uppercase tracking-[0.3em] text-accent-orange/70 mb-2">
            {item.enneagram_type}
          </span>
          <h4 className="text-xs md:text-sm font-bold text-[#EAEAEA] leading-tight line-clamp-3 px-1">
            {item.title}
          </h4>
          {!item.image && (
            <span className="mt-3 text-[8px] font-mono uppercase tracking-[0.3em] text-neutral-600">
              Poster em breve
            </span>
          )}
        </div>

        {item.image && (
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            className={`w-full h-full object-cover transition-opacity duration-700 ${
              loaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}

        {/* Bottom gradient for legibility (only when image is shown) */}
        {item.image && (
          <div
            className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent transition-opacity duration-500"
            style={{ opacity: loaded ? 0.85 : 0 }}
          ></div>
        )}

        {/* Top-right media-type badge */}
        <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/70 backdrop-blur-sm border border-white/15 rounded-full text-white text-[10px] font-mono uppercase tracking-widest z-10">
          {item.type}
        </div>
        {/* Top-left enneagram badge */}
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-accent-orange/95 backdrop-blur-sm rounded-full text-white text-[10px] font-mono uppercase tracking-widest shadow-lg shadow-accent-orange/30 z-10">
          {item.enneagram_type}
        </div>

        {/* Bottom content — title (no play icon) */}
        {item.image && loaded && (
          <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 z-10">
            <h3 className="font-bold text-sm md:text-base text-white leading-tight line-clamp-2 drop-shadow-md">
              {item.title}
            </h3>
            <p className="text-[10px] text-white/70 line-clamp-1 mt-1">
              {item.description || item.enneagram_type}
            </p>
          </div>
        )}
      </div>
    </Link>
  );
};

const EneaflixPage = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    // 1) Try sessionStorage cache — instant render path for repeat visits.
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.ts && Date.now() - parsed.ts < CACHE_TTL_MS) {
          setItems(parsed.items || []);
          setLoading(false);
          return;
        }
      }
    } catch {
      /* sessionStorage may be unavailable in private mode */
    }
    // 2) Network — listing endpoint returns thumbs only.
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/eneaflix`);
        setItems(data || []);
        try {
          sessionStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ ts: Date.now(), items: data || [] }),
          );
        } catch {/* ignore */}
      } catch (e) {
        console.error('Error loading eneaflix:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredItems = useMemo(() => (
    selectedType === 'all'
      ? items
      : items.filter((it) => it.enneagram_type === selectedType)
  ), [items, selectedType]);

  // Reset pagination whenever the type filter changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedType]);

  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < filteredItems.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-orange"></div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative z-10 py-24 border-b border-neutral-900" style={{ background: 'rgba(10, 10, 10, 0.78)' }}>
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-gray-400 mb-4 block">
                {t('pages.eneaflix.hero_label')}
              </span>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-[1.15] pb-1">
                <EneaflixWordmark />
              </h1>
              <p className="text-lg text-neutral-400">
                {t('pages.eneaflix.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="relative z-10 py-8 border-b border-neutral-900" style={{ background: 'rgba(17, 17, 17, 0.85)' }}>
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setSelectedType('all')}
                data-testid="eneaflix-filter-all"
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  selectedType === 'all'
                    ? 'bg-accent-orange text-white'
                    : 'bg-[#1F1F1F] text-neutral-400 hover:text-white hover:border-accent-orange/40'
                }`}
              >
                {t('pages.eneaflix.all_types')}
              </button>
              {ENNEAGRAM_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  data-testid={`eneaflix-filter-${type.replace(' ', '-').toLowerCase()}`}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                    selectedType === type
                      ? 'bg-accent-orange text-white'
                      : 'bg-[#1F1F1F] text-neutral-400 hover:text-white'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="relative z-10 py-24" style={{ background: 'rgba(10, 10, 10, 0.78)' }}>
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {visibleItems.map((item) => (
                <PosterCard key={item.eneaflix_id} item={item} />
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-12">
                <button
                  data-testid="eneaflix-load-more"
                  onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                  className="px-8 py-3.5 bg-transparent border border-accent-orange/50 text-accent-orange rounded-full text-sm font-bold uppercase tracking-wider hover:bg-accent-orange hover:text-white transition-all inline-flex items-center gap-2"
                >
                  Carregar mais
                  <span className="text-xs font-mono text-accent-orange/70 group-hover:text-white">
                    ({filteredItems.length - visibleCount})
                  </span>
                  <iconify-icon icon="solar:arrow-down-linear"></iconify-icon>
                </button>
              </div>
            )}

            {filteredItems.length === 0 && (
              <div className="text-center py-12 text-neutral-500">
                <iconify-icon icon="solar:videocamera-linear" className="text-6xl mb-4"></iconify-icon>
                <p>{t('pages.eneaflix.no_content')}</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EneaflixPage;
